import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import apiUrls from "../../../api";
import type { RootState } from "../../../common/state/store";
import type { HelpArticle } from "src/common/components/HelpArticleSection/useHelpArticle";

export type HelpArticleStatus = "loading" | "ready" | "none";

interface HelpArticleEntry {
  status: HelpArticleStatus;
  article: HelpArticle | null;
}

interface HelpArticlesState {
  byKey: Record<string, HelpArticleEntry>;
}

const initialState: HelpArticlesState = { byKey: {} };

export const helpArticleKey = (lang: string, modelName: string) => `${lang}::${modelName}`;

/**
 * Fetch (and cache) the mapped HelpDocs article for one content type. Dispatched
 * lazily by useHelpArticle when a node form opens. The `condition` guard makes it
 * idempotent: a key that's already loading/ready/none is never refetched within
 * the session.
 */
export const fetchHelpArticle = createAsyncThunk<
  { key: string; article: HelpArticle | null },
  { modelName: string; lang: string },
  { state: RootState }
>(
  "helpArticles/fetch",
  async ({ modelName, lang }) => {
    const response = await axios.get<{ article: HelpArticle | null }>(
      `${apiUrls.helpArticles}/${modelName}?lang=${lang}`,
    );
    return { key: helpArticleKey(lang, modelName), article: response.data?.article ?? null };
  },
  {
    condition: ({ modelName, lang }, { getState }) => {
      if (!modelName) return false;
      return !getState().helpArticles.byKey[helpArticleKey(lang, modelName)];
    },
  },
);

const helpArticlesSlice = createSlice({
  name: "helpArticles",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHelpArticle.pending, (state, action) => {
        const { modelName, lang } = action.meta.arg;
        state.byKey[helpArticleKey(lang, modelName)] = { status: "loading", article: null };
      })
      .addCase(
        fetchHelpArticle.fulfilled,
        (state, action: PayloadAction<{ key: string; article: HelpArticle | null }>) => {
          const { key, article } = action.payload;
          state.byKey[key] = { status: article ? "ready" : "none", article };
        },
      )
      .addCase(fetchHelpArticle.rejected, (state, action) => {
        const { modelName, lang } = action.meta.arg;
        state.byKey[helpArticleKey(lang, modelName)] = { status: "none", article: null };
      });
  },
});

export const selectHelpArticleEntry =
  (lang: string, modelName: string) =>
  (state: RootState): HelpArticleEntry | undefined =>
    state.helpArticles.byKey[helpArticleKey(lang, modelName)];

export default helpArticlesSlice.reducer;
